import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaChartLine, FaCalendarAlt, FaMoneyBillWave, FaBalanceScale } from 'react-icons/fa';

const NFPPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'NFP Trading Fund' }
  ];

  const strategies = [
    {
      title: "Pre-NFP Positioning",
      description: "Strategic positioning prior to NFP releases based on market sentiment and advanced economic forecasting models."
    },
    {
      title: "Post-Release Volatility Capture",
      description: "Rapid execution strategies designed to capitalize on the immediate market reaction following NFP data releases."
    },
    {
      title: "Trend Following",
      description: "Identifying and following market trends established after initial NFP volatility has subsided."
    },
    {
      title: "Cross-Market Arbitrage",
      description: "Exploiting price differentials across related markets influenced by NFP data, including currencies, equities, and fixed income."
    }
  ];

  const keyFeatures = [
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Data-Driven Analysis",
      description: "Sophisticated quantitative models analyzing historical NFP patterns and their market impact to inform trading decisions."
    },
    {
      icon: <FaCalendarAlt className="w-8 h-8 text-secondary" />,
      title: "Event-Focused Strategy",
      description: "Trading strategies specifically designed around major economic data releases, with NFP as the primary focus."
    },
    {
      icon: <FaMoneyBillWave className="w-8 h-8 text-secondary" />,
      title: "Liquidity Management",
      description: "Advanced liquidity management techniques to execute large positions during high-volatility periods without market impact."
    },
    {
      icon: <FaBalanceScale className="w-8 h-8 text-secondary" />,
      title: "Risk Calibration",
      description: "Dynamic risk controls that adjust exposure based on market volatility expectations around NFP release dates."
    }
  ];

  return (
    <>
      <PageHeader 
        title="NFP Trading Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Specialized NFP Event Trading Strategy
              </h2>
              <p className="text-lg text-gray-700">
                The Segway Capital NFP Trading Fund is a specialized investment strategy focused on capturing market opportunities created by the monthly Non-Farm Payroll (NFP) report - one of the most significant and market-moving economic indicators.
              </p>
              <p className="text-lg text-gray-700">
                Our expert team combines deep macroeconomic understanding with sophisticated algorithmic trading techniques to generate significant returns during these predictable periods of market volatility.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Speak to an Investment Advisor
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/pen-calculator-colored-paper-with-words-non-farm-payroll_694006-4873.jpg_vwtxun.jpg" 
                alt="NFP Trading Analysis" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our NFP Trading Approach
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We employ multiple complementary strategies to maximize returns around NFP data releases, each targeting different aspects of market behavior.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
            {strategies.map((strategy, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md border-l-4 border-secondary"
              >
                <h4 className="text-xl font-heading font-semibold mb-3">{strategy.title}</h4>
                <p className="text-gray-600">{strategy.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/business-people-brainstorming-office_1048944-25233953.jpg_amz21m.jpg" 
                alt="NFP Strategy Team" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary">
                Why NFP Trading?
              </h3>
              <p className="text-lg text-gray-700">
                The Non-Farm Payroll report creates unique trading opportunities:
              </p>
              
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="text-xl font-heading font-semibold mb-2 text-primary">Predictable Volatility</h4>
                  <p className="text-gray-600">NFP releases consistently generate significant market volatility, creating trading opportunities across multiple asset classes.</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="text-xl font-heading font-semibold mb-2 text-primary">Market Inefficiencies</h4>
                  <p className="text-gray-600">The complex nature of NFP data interpretation creates pricing inefficiencies that can be exploited by sophisticated algorithms.</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h4 className="text-xl font-heading font-semibold mb-2 text-primary">Limited Exposure Periods</h4>
                  <p className="text-gray-600">Trading risk is concentrated around 12 annual events, allowing for precise risk management and capital efficiency.</p>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-8">
              Key Fund Features
            </h3>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">$120M+</div>
                <div className="text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">32.7%</div>
                <div className="text-gray-300">Average Annual Return</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">1.94</div>
                <div className="text-gray-300">Sharpe Ratio</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NFPPage;
